import { useState, useMemo, useEffect } from 'react';
import {
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { Search, Download } from 'lucide-react';
import { Input, FormField, Label } from '../ui/input';
import { Select } from '../ui/select-custom';
import { Button } from '../ui/button';
import { CsvPreviewDialog } from '../dialogs/csv-preview-dialog';
import { auth } from '../../lib/firebase';
import { getAllBudgets, getAllIncomes } from '../../services/firestore';
import { categoryTypes } from '../../types';
import { formatCurrency } from '../../lib/format';
import type { Expense, Tag, CategoryType, Budget, Income } from '../../types';

const VISIBLE_CATEGORIES: CategoryType[] = ['needs', 'comfort'];

const MONTH_NAMES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];
const MONTH_NAMES_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function monthOptions() {
  return MONTH_NAMES_FULL.map((label, i) => ({ value: String(i), label }));
}

function yearOptions(expenses: Expense[]) {
  const now = new Date().getFullYear();
  const min = expenses.length
    ? Math.min(...expenses.map((e) => new Date(e.date).getFullYear()))
    : now - 2;
  const years: { value: string; label: string }[] = [];
  for (let y = now; y >= Math.min(min, now - 5); y--) {
    years.push({ value: String(y), label: String(y) });
  }
  return years;
}

interface ExpenseReportsProps {
  expenses: Expense[];
  availableTags: Tag[];
}

interface TooltipEntry {
  name?: string;
  value?: number;
  color?: string;
  stroke?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-white px-3 py-2 shadow text-xs">
      <p className="font-medium text-foreground mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color ?? p.stroke }}>
          {p.name}: R$ {formatCurrency(p.value ?? 0)}
        </p>
      ))}
    </div>
  );
};

export function ExpenseReports({
  expenses,
  availableTags,
}: ExpenseReportsProps) {
  const now = new Date();
  const [allBudgets, setAllBudgets] = useState<Budget[]>([]);
  const [allIncomes, setAllIncomes] = useState<Income[]>([]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    getAllBudgets(user.uid)
      .then(setAllBudgets)
      .catch(() => {});
    getAllIncomes(user.uid)
      .then(setAllIncomes)
      .catch(() => {});
  }, []);

  const [search, setSearch] = useState('');
  const [fromMonth, setFromMonth] = useState(
    String(now.getMonth() < 5 ? 0 : now.getMonth() - 5)
  );
  const [fromYear, setFromYear] = useState(
    String(now.getMonth() < 5 ? now.getFullYear() - 1 : now.getFullYear())
  );
  const [toMonth, setToMonth] = useState(String(now.getMonth()));
  const [toYear, setToYear] = useState(String(now.getFullYear()));
  const [selectedCategory, setSelectedCategory] = useState<
    CategoryType | 'all'
  >('all');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [chartGrouping, setChartGrouping] = useState<'month' | 'day'>('month');
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);

  const toggleTag = (id: string) =>
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );

  const from = new Date(Number(fromYear), Number(fromMonth), 1);
  const to = new Date(Number(toYear), Number(toMonth) + 1, 0, 23, 59, 59, 999);

  const filtered = useMemo(() => {
    return expenses.filter((e) => {
      const date = new Date(e.date);
      if (date < from || date > to) return false;
      if (selectedCategory !== 'all' && e.categoryType !== selectedCategory)
        return false;
      if (
        selectedTagIds.length > 0 &&
        !selectedTagIds.every((id) => e.tags?.includes(id))
      )
        return false;
      const q = search.toLowerCase();
      if (
        q &&
        !e.title.toLowerCase().includes(q) &&
        !e.description?.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [expenses, from, to, selectedCategory, selectedTagIds, search]);

  const totalFiltered = filtered.reduce((s, e) => s + e.value, 0);

  const totalMonths = useMemo(() => {
    const fy = Number(fromYear),
      fm = Number(fromMonth);
    const ty = Number(toYear),
      tm = Number(toMonth);
    return Math.max(1, (ty - fy) * 12 + (tm - fm) + 1);
  }, [fromYear, fromMonth, toYear, toMonth]);

  const avgPerMonth = totalFiltered / totalMonths;

  // Incomes within the selected period (incomes are stored per month/year, not by date)
  const totalIncomes = useMemo(() => {
    const fromKey = Number(fromYear) * 12 + Number(fromMonth);
    const toKey = Number(toYear) * 12 + Number(toMonth);
    return allIncomes
      .filter((i) => {
        const key = i.year * 12 + (i.month - 1);
        return key >= fromKey && key <= toKey;
      })
      .reduce((s, i) => s + i.value, 0);
  }, [allIncomes, fromYear, fromMonth, toYear, toMonth]);

  const avgIncomePerMonth = totalIncomes / totalMonths;

  // Spending by month with per-month budget
  const byMonth = useMemo(() => {
    const map: Record<string, { needs: number; comfort: number }> = {};
    filtered.forEach((e) => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
      if (!map[key]) map[key] = { needs: 0, comfort: 0 };
      if (e.categoryType === 'needs') map[key]!.needs += e.value;
      else if (e.categoryType === 'comfort') map[key]!.comfort += e.value;
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, vals]) => {
        const [y, m] = key.split('-').map(Number);
        const monthBudget = allBudgets.find(
          (b) => b.year === y && b.month === m! + 1
        );
        const needsBudget = monthBudget?.needs ?? null;
        const comfortBudget = monthBudget?.comfort ?? null;
        const freedomBudget = monthBudget?.freedom ?? null;
        const leftover =
          monthBudget != null
            ? monthBudget.needs +
              monthBudget.comfort +
              monthBudget.freedom -
              vals.needs -
              vals.comfort
            : null;
        return {
          month: `${MONTH_NAMES[m!]}/${String(y).slice(2)}`,
          ...vals,
          needsBudget,
          comfortBudget,
          freedomBudget,
          leftover,
        };
      });
  }, [filtered, allBudgets]);

  const showLeftover =
    selectedTagIds.length === 0 && selectedCategory === 'all' && search === '';

  const totalLeftover = byMonth.reduce((s, r) => s + (r.leftover ?? 0), 0);
  const totalExpectedFreedom = byMonth.reduce(
    (s, r) => s + (r.freedomBudget ?? 0),
    0
  );
  const freedomDiff = totalLeftover - totalExpectedFreedom;

  // Spending by day
  const byDay = useMemo(() => {
    const map: Record<string, { needs: number; comfort: number }> = {};
    filtered.forEach((e) => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map[key]) map[key] = { needs: 0, comfort: 0 };
      if (e.categoryType === 'needs') map[key]!.needs += e.value;
      else if (e.categoryType === 'comfort') map[key]!.comfort += e.value;
    });

    const result = [];
    const cursor = new Date(Number(fromYear), Number(fromMonth), 1);
    const end = new Date(Number(toYear), Number(toMonth) + 1, 0);
    while (cursor <= end) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
      const vals = map[key] ?? { needs: 0, comfort: 0 };
      result.push({
        month: `${String(cursor.getDate()).padStart(2, '0')}/${String(cursor.getMonth() + 1).padStart(2, '0')}`,
        ...vals,
        needsBudget: null,
        comfortBudget: null,
        freedomBudget: null,
        leftover: null,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return result;
  }, [filtered, fromYear, fromMonth, toYear, toMonth]);

  // Spending by category
  const byCategory = useMemo(() => {
    return VISIBLE_CATEGORIES.map((key) => ({
      name: categoryTypes[key].label,
      value: filtered
        .filter((e) => e.categoryType === key)
        .reduce((s, e) => s + e.value, 0),
      color: categoryTypes[key].color,
    })).filter((c) => c.value > 0);
  }, [filtered]);

  // Spending by tag
  const byTag = useMemo(() => {
    return availableTags
      .map((tag) => ({
        name: `${tag.icon} ${tag.name}`,
        value: filtered
          .filter((e) => e.tags?.includes(tag.id))
          .reduce((s, e) => s + e.value, 0),
      }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [filtered, availableTags]);

  const sortedFiltered = [...filtered].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const years = yearOptions(expenses);

  return (
    <div className="flex flex-col gap-6">
      {/* Filters */}
      <div className="flex flex-col gap-4 rounded-xl border border-border bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <FormField>
            <Label>Busca</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Título ou descrição…"
                className="pl-9"
              />
            </div>
          </FormField>

          <FormField>
            <Label>De</Label>
            <div className="flex gap-2">
              <Select
                value={fromMonth}
                onChange={setFromMonth}
                options={monthOptions()}
              />
              <Select value={fromYear} onChange={setFromYear} options={years} />
            </div>
          </FormField>

          <FormField>
            <Label>Até</Label>
            <div className="flex gap-2">
              <Select
                value={toMonth}
                onChange={setToMonth}
                options={monthOptions()}
              />
              <Select value={toYear} onChange={setToYear} options={years} />
            </div>
          </FormField>

          <FormField>
            <Label>Categoria</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelectedCategory('all')}
                className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${selectedCategory === 'all' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
              >
                Todas
              </button>
              {VISIBLE_CATEGORIES.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() =>
                    setSelectedCategory(selectedCategory === key ? 'all' : key)
                  }
                  className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${selectedCategory === key ? 'border-transparent text-white' : 'border-border text-muted-foreground hover:bg-muted'}`}
                  style={
                    selectedCategory === key
                      ? { backgroundColor: categoryTypes[key].color }
                      : undefined
                  }
                >
                  {categoryTypes[key].label}
                </button>
              ))}
            </div>
          </FormField>
        </div>

        {availableTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => {
              const active = selectedTagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-foreground-subtle hover:bg-muted'}`}
                >
                  <span>{tag.icon}</span>
                  <span>{tag.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-border bg-white p-4">
          <p className="text-xs text-muted-foreground">Total gasto no período</p>
          <p className="mt-1 text-xl font-bold text-red-500">
            R$ {formatCurrency(totalFiltered)}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <p className="text-xs text-muted-foreground">Média de gastos por mês</p>
          <p className="mt-1 text-xl font-bold text-foreground">
            R$ {formatCurrency(avgPerMonth)}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <p className="text-xs text-muted-foreground">Total ganho no período</p>
          <p className="mt-1 text-xl font-bold text-green-600">
            R$ {formatCurrency(totalIncomes)}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <p className="text-xs text-muted-foreground">Média de rendas por mês</p>
          <p className="mt-1 text-xl font-bold text-foreground">
            R$ {formatCurrency(avgIncomePerMonth)}
          </p>
        </div>
        {showLeftover && (
          <div className="rounded-xl border border-border bg-white p-4">
            <p className="text-xs text-muted-foreground">Sobrou no período</p>
            <p
              className={`mt-1 text-xl font-bold ${totalLeftover >= 0 ? 'text-green-600' : 'text-red-500'}`}
            >
              R$ {formatCurrency(totalLeftover)}
            </p>
          </div>
        )}
        {showLeftover && (
          <div className="rounded-xl border border-border bg-white p-4">
            <p className="text-xs text-muted-foreground">
              vs. Liberdade esperada
            </p>
            <p
              className={`mt-1 text-xl font-bold ${freedomDiff >= 0 ? 'text-green-600' : 'text-red-500'}`}
            >
              {freedomDiff >= 0 ? '+' : ''}R$ {formatCurrency(freedomDiff)}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              esperado R$ {formatCurrency(totalExpectedFreedom)}
            </p>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16">
          <p className="text-sm text-muted-foreground">
            Nenhum gasto encontrado com os filtros aplicados
          </p>
        </div>
      ) : (
        <>
          {/* Charts row */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* ComposedChart: grouped bars by category + budget lines per month */}
            <div className="lg:col-span-2 rounded-xl border border-border bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-foreground">
                    {chartGrouping === 'month' ? 'Gastos por mês' : 'Gastos por dia'}
                  </p>
                  <Select
                    value={chartGrouping}
                    onChange={(v) => setChartGrouping(v as 'month' | 'day')}
                    options={[
                      { value: 'month', label: 'Mês' },
                      { value: 'day', label: 'Dia' },
                    ]}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {VISIBLE_CATEGORIES.map((key) => (
                    <span
                      key={key}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <span
                        className="size-2.5 rounded-full"
                        style={{ backgroundColor: categoryTypes[key].color }}
                      />
                      {categoryTypes[key].label}
                    </span>
                  ))}
                  {chartGrouping === 'month' && (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span
                        className="inline-block w-5 border-t-2 border-dashed"
                        style={{ borderColor: categoryTypes.needs.color }}
                      />
                      Limite Fixos
                    </span>
                  )}
                  {chartGrouping === 'month' && (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span
                        className="inline-block w-5 border-t-2 border-dashed"
                        style={{ borderColor: categoryTypes.comfort.color }}
                      />
                      Limite Conforto
                    </span>
                  )}
                  {showLeftover && chartGrouping === 'month' && (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span
                        className="size-2.5 rounded-sm"
                        style={{ backgroundColor: '#22c55e' }}
                      />
                      Sobrou
                    </span>
                  )}
                  {showLeftover && chartGrouping === 'month' && (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span
                        className="inline-block w-5 border-t-2 border-dashed"
                        style={{ borderColor: '#a855f7' }}
                      />
                      Liberdade esperada
                    </span>
                  )}
                </div>
              </div>
              {(chartGrouping === 'month' ? byMonth : byDay).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart
                    data={chartGrouping === 'month' ? byMonth : byDay}
                    margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
                    barCategoryGap="25%"
                  >
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v: number) =>
                        v >= 1000
                          ? `R$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`
                          : `R$${v}`
                      }
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="needs"
                      name={categoryTypes.needs.label}
                      fill={categoryTypes.needs.color}
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      dataKey="comfort"
                      name={categoryTypes.comfort.label}
                      fill={categoryTypes.comfort.color}
                      radius={[3, 3, 0, 0]}
                    />
                    {chartGrouping === 'month' && (
                      <Line
                        type="monotone"
                        dataKey="needsBudget"
                        name="Limite Fixos"
                        stroke={categoryTypes.needs.color}
                        strokeDasharray="5 3"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    )}
                    {chartGrouping === 'month' && (
                      <Line
                        type="monotone"
                        dataKey="comfortBudget"
                        name="Limite Conforto"
                        stroke={categoryTypes.comfort.color}
                        strokeDasharray="5 3"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    )}
                    {showLeftover && chartGrouping === 'month' && (
                      <Bar
                        dataKey="leftover"
                        name="Sobrou"
                        fill="#22c55e"
                        radius={[3, 3, 0, 0]}
                      />
                    )}
                    {showLeftover && chartGrouping === 'month' && (
                      <Line
                        type="monotone"
                        dataKey="freedomBudget"
                        name="Liberdade esperada"
                        stroke="#a855f7"
                        strokeDasharray="5 3"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-xs text-muted-foreground">Sem dados</p>
              )}
            </div>

            {/* Pie: by category */}
            <div className="rounded-xl border border-border bg-white p-4">
              <p className="text-sm font-semibold text-foreground mb-4">
                Por categoria
              </p>
              {byCategory.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={byCategory}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {byCategory.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v) => `R$ ${formatCurrency(Number(v))}`}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      formatter={(v) => (
                        <span className="text-xs text-foreground">{v}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-xs text-muted-foreground">Sem dados</p>
              )}
            </div>
          </div>

          {/* Bar: by tag */}
          {byTag.length > 0 && (
            <div className="rounded-xl border border-border bg-white p-4">
              <p className="text-sm font-semibold text-foreground mb-4">
                Por tag
              </p>
              <ResponsiveContainer
                width="100%"
                height={Math.max(120, byTag.length * 36)}
              >
                <BarChart
                  data={byTag}
                  layout="vertical"
                  margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
                >
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `R$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`
                        : `R$${v}`
                    }
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={90}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="value"
                    name="Gasto"
                    fill="#F4A261"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Table */}
          <div className="rounded-xl border border-border bg-white overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
              <p className="text-sm font-semibold text-foreground">
                Detalhamento
              </p>
              <div className="flex items-center gap-3">
                <p className="text-xs text-muted-foreground">
                  {filtered.length} gastos · R$ {formatCurrency(totalFiltered)}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={sortedFiltered.length === 0}
                  onClick={() => setCsvPreviewOpen(true)}
                >
                  <Download />
                  Baixar CSV
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                      Data
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                      Título
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                      Categoria
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                      Tags
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">
                      Valor
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiltered.map((e) => {
                    const cat = categoryTypes[e.categoryType];
                    const tags = availableTags.filter((t) =>
                      e.tags?.includes(t.id)
                    );
                    return (
                      <tr
                        key={e.id}
                        className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(e.date).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                          })}
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="text-xs font-medium text-foreground truncate max-w-45">
                            {e.title}
                          </p>
                          {e.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-45">
                              {e.description}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="flex items-center gap-1.5">
                            <span
                              className="size-2 rounded-full shrink-0"
                              style={{ backgroundColor: cat.color }}
                            />
                            <span className="text-xs text-foreground">
                              {cat.label}
                            </span>
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="flex items-center gap-1">
                            {tags.map((t) => (
                              <span
                                key={t.id}
                                title={t.name}
                                className="text-sm leading-none"
                              >
                                {t.icon}
                              </span>
                            ))}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs font-semibold text-foreground whitespace-nowrap">
                          R$ {formatCurrency(e.value)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/30 border-t border-border">
                    <td
                      colSpan={4}
                      className="px-4 py-2.5 text-xs font-semibold text-foreground"
                    >
                      Total
                    </td>
                    <td className="px-4 py-2.5 text-right text-sm font-bold text-foreground">
                      R$ {formatCurrency(totalFiltered)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}

      <CsvPreviewDialog
        open={csvPreviewOpen}
        onClose={() => setCsvPreviewOpen(false)}
        expenses={sortedFiltered}
        availableTags={availableTags}
        filename="relatorio-gastos.csv"
      />
    </div>
  );
}
